import type {Entitlement} from '@packages/domains/billing/types';
import {PageRecordType} from '@packages/systems/core/records/PageRecord';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {
  AudienceOptimizationJson,
  ExperienceOptimizationJson,
  MetricOptimizationJson,
  OptimizationJson,
  OptimizationState,
  OptimizationVariation,
  ScopedMetricJson,
} from '@packages/systems/optimization/types';
import {WebflowPlaybookId} from '@intellimize/dash-shared';
import {getOnlyContainsText} from '@packages/systems/optimization/utils';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  isButton,
  isFormButtonElement,
  isLink,
  isTextHolder,
} from '@packages/utilities/elementTypeUtils';
import {emptyArray, emptyObject} from '@packages/utilities/fp/utils';
import {
  getClosestValidElementForOptimization,
  isValidElementForOptimization,
} from '@packages/utilities/platform';
import {
  selectCurrentComponentRender,
  selectSelectedElement,
} from '@packages/utilities/platform-selectors';
import {createSelector} from 'reselect';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {TEMPORARY_EXPERIENCE_ID} from '@packages/systems/optimization/constants';

type ElementIsType = (element: ExpressionElement) => boolean;

const ctaElements: ElementIsType[] = [
  isLink,
  isButton,
  isFormButtonElement,
  isTextHolder,
];
const textElements: ElementIsType[] = [isTextHolder];
const allowedPlaybookElements: Record<WebflowPlaybookId, ElementIsType[]> = {
  non_commital_cta: ctaElements,
  headline: textElements,
  social_proof: textElements,
  plain_language: textElements,
  landing_page_2nd_person: textElements,
  user_guided_text_change: textElements,
};

export const selectIsEditingClickMetric = (
  store: OptimizationState
): boolean => {
  return store.currentMetric !== null;
};

export const selectCurrentClickMetricEventId = (
  store: OptimizationState
): string | null => {
  return store.currentMetric?.eventIds[0] ?? null;
};

export const selectCurrentClickMetric = (
  state: OptimizationState
): OptimizationState['currentMetric'] => {
  return state.currentMetric;
};

export const selectCurrentClickMetricJson = (
  optimizationJson: OptimizationJson | undefined,
  state: OptimizationState
): MetricOptimizationJson | null => {
  const experience = selectCurrentExperience(optimizationJson, state);
  if (experience && state.currentMetric) {
    return (
      experience.metrics.find(
        (metric) => metric.id === state.currentMetric?.id
      ) ?? null
    );
  } else if (state.currentMetric) {
    return (
      optimizationJson?.metrics.find(
        (metric) => metric.id === state.currentMetric?.id
      ) ?? null
    );
  }
  return null;
};

export const selectCurrentExperience = (
  optimizationJson: OptimizationJson | undefined,
  {currentExperienceId}: OptimizationState
): ExperienceOptimizationJson | null => {
  if (!currentExperienceId || !optimizationJson) {
    return null;
  }

  return optimizationJson?.experiences[currentExperienceId] ?? null;
};

// FIXME:
// eslint-disable-next-line webflow/selector-memoization
export const selectVariations = (
  optimizationJson: OptimizationJson | undefined,
  state: OptimizationState
): OptimizationVariation[] => {
  if (!optimizationJson || !state.currentExperienceId) {
    return emptyArray;
  }

  const experience = selectCurrentExperience(optimizationJson, state);

  const variations = Object.values(experience?.variations || {});

  if (variations.length === 0) {
    return emptyArray;
  }

  return variations;
};

export const selectCurrentVariation = (
  optimizationJson: OptimizationJson | undefined,
  state: OptimizationState
) => {
  const {currentExperienceId, currentVariationId} = state;
  if (!optimizationJson || !currentVariationId || !currentExperienceId) {
    return null;
  }

  return (
    optimizationJson?.experiences[currentExperienceId]?.variations[
      currentVariationId
    ] ?? null
  );
};

export const selectBaseVariationId = (
  optimizationJson: OptimizationJson | undefined,
  state: OptimizationState
) => {
  return (
    selectVariations(optimizationJson, state).find(
      (variation) => variation.isBaseVariation
    )?.id ?? null
  );
};

export const selectCurrentVariationIsBase = (state: OptimizationState) => {
  return state.isCurrentVariationBase;
};

// FIXME:
// eslint-disable-next-line webflow/selector-memoization
export const selectCurrentExperienceAndVariation = (
  json: OptimizationJson | undefined,
  state: OptimizationState
) => {
  return {
    experience: selectCurrentExperience(json, state),
    variation: selectCurrentVariation(json, state),
  };
};

export const selectOptimizeMode = (state: OptimizationState) => {
  if (!state) {
    return 'out-of-optimize';
  }

  return state.currentVariationId ? 'in-optimize' : 'out-of-optimize';
};

export const selectIsInOptimizeMode = (state: OptimizationState) => {
  return selectOptimizeMode(state) === 'in-optimize';
};

export const selectCurrentExperienceId = (state: OptimizationState) => {
  return state.currentExperienceId;
};

export const selectCurrentVariationId = (state: OptimizationState) => {
  return state.currentVariationId;
};

export const selectAllAudiences = (
  customerJson: OptimizationJson | undefined
): Record<string, AudienceOptimizationJson> => {
  return customerJson?.audiences || emptyObject;
};

export const selectCurrentModal = (
  state: OptimizationState
): OptimizationState['currentModal'] => {
  return state.currentModal;
};

export const selectShowOptimizationCreationTooltip = (
  state: OptimizationState
): boolean => {
  return state.viewParams.showOptimizationCreationTooltip;
};

export const selectMaxOptimizationsEntitlement = (
  state: DesignerStores
): Entitlement | undefined =>
  state.EntitlementsStore?.entitlements?.maxOptimizations;

export const selectIsDataCollectionEnabled = (state: DesignerStores) =>
  state.OptimizationStore.dataCollectionEnabled;

export const selectCurrentReportGoal = (
  state: DesignerStores
): ScopedMetricJson | undefined =>
  state.OptimizationAnalyticsStore.viewParams.optimizationReport.selectedGoal;

export const selectIsPublishedDataCollectionEnabled = (state: DesignerStores) =>
  state.OptimizationStore.publishedDataCollectionEnabled;

export const selectIsAiSuggestionChatOpen = (state: OptimizationState) =>
  state.isAiSuggestionChatOpen;

export const selectCurrentPlaybookId = (
  state: DesignerStores
): WebflowPlaybookId | null => state.OptimizationStore.currentPlaybookId;

export const selectPlaybookCustomPrompt = (state: OptimizationState) =>
  state.playbookCustomPrompt;

export function getIsValidSuggestionSelection(
  playbookId: WebflowPlaybookId | null,
  selectedElement: ExpressionElement | null,
  currentPage: PageRecordType | null | undefined,
  focusedSymbolNativeIdPath: string[] | null,
  canSuggestInComponentMain: boolean
) {
  const canAccessAiAssistantCustomPrompts = getFeatureFlagOutsideOfReact(
    FEATURE_FLAGS.OPTIMIZATION_AI_ASSISTANT_CUSTOM_PROMPTS
  );
  if ((!playbookId && !canAccessAiAssistantCustomPrompts) || !selectedElement) {
    return false;
  }
  const isComponentRoot =
    focusedSymbolNativeIdPath?.[focusedSymbolNativeIdPath.length - 1] ===
    selectedElement.id;
  if (isComponentRoot) {
    return false;
  }
  const isCmsPage = Boolean(currentPage?.dynCollection);
  if (isCmsPage) {
    return false;
  }
  const isValid = isValidElementForOptimization(selectedElement);
  if (!isValid) {
    return false;
  }
  const onlyContainsText = getOnlyContainsText(selectedElement);
  if (!onlyContainsText) {
    return false;
  }
  const isInComponentMain = (focusedSymbolNativeIdPath?.length || 0) !== 0;
  if (!canSuggestInComponentMain && isInComponentMain) {
    return false;
  }
  const isAllowedElement = allowedPlaybookElements[
    playbookId ?? 'user_guided_text_change'
  ]?.some((isAllowed) => selectedElement && isAllowed(selectedElement));
  if (!isAllowedElement) {
    return false;
  }
  return true;
}

export const selectClosestValidElementForOptimization = createSelector(
  selectCurrentComponentRender,
  selectSelectedElement,
  (componentRender, selectedElement) => {
    if (!componentRender || !selectedElement) {
      return null;
    }
    const closestElement = getClosestValidElementForOptimization(
      componentRender,
      selectedElement.id
    );
    return closestElement;
  }
);

export const selectIsExperiencePopoverOpen = (state: OptimizationState) =>
  state.isExperiencePopoverOpen;

export const selectOptimizationStore = (state: DesignerStores) =>
  state.OptimizationStore;

export const selectOptimizationChangedPopoverIsOpen = (
  state: OptimizationState
): OptimizationState['optimizationChangedPopoverIsOpen'] => {
  return state.optimizationChangedPopoverIsOpen;
};

export const selectIsApplyingOptimizationWrappers = (
  state: OptimizationState
): OptimizationState['isApplyingOptimizationWrappers'] => {
  return state.isApplyingOptimizationWrappers;
};

export const selectWrapperPublishingOptimizationName = (
  state: OptimizationState
): OptimizationState['wrapperPublishingOptimizationName'] => {
  return state.wrapperPublishingOptimizationName;
};

export const selectShouldPublishApplyVariation = (
  state: OptimizationState
): OptimizationState['shouldPublishApplyVariation'] => {
  return state.shouldPublishApplyVariation;
};

export const selectIsPublishingApplyVariation = (
  state: OptimizationState
): OptimizationState['isPublishingApplyVariation'] => {
  return state?.isPublishingApplyVariation ?? false;
};

export const selectIsCurrentExperienceTemporary = (
  state: OptimizationState
): boolean => {
  return state.currentExperienceId === TEMPORARY_EXPERIENCE_ID;
};
